import styled from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';
import { colors, fonts } from '../theme';
import { HudPanel, Eyebrow, StatusDot, SectionBackdrop } from '../components/HudFrame';
import aboutImg from '../assets/space4.jpg';
import { fadeUp, staggerContainer, revealViewport } from '../motion';

const Wrap = styled.section`
  position: relative;
`;

const Main = styled.div`
  position: relative;
  z-index: 1;
  max-width: 860px;
  margin: 0 auto;
  padding: 4.5rem 2rem 5rem;
  color: ${colors.textPrimary};

  @media (max-width: 768px) {
    padding: 3rem 1.25rem 3.5rem;
  }
`;

const Title = styled.h2`
  font-family: ${fonts.display};
  font-size: 2.6rem;
  margin-bottom: 2rem;
  color: ${colors.textPrimary};
  letter-spacing: 0.06em;
  text-shadow: 0 4px 32px rgba(0,0,0,0.65);

  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
    letter-spacing: 0.03em;
  }
`;

const Panel = styled(motion(HudPanel))`
  padding: 2rem;
  background: rgba(6, 9, 17, 0.68);

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const Text = styled.p`
  font-family: ${fonts.mono};
  font-size: 1rem;
  line-height: 1.8;
  margin-bottom: 1.5rem;
  color: ${colors.textDim};

  @media (max-width: 768px) {
    font-size: 0.9rem;
    line-height: 1.7;
    margin-bottom: 1.25rem;
  }

  &:last-child {
    margin-bottom: 0;
  }

  strong {
    color: ${colors.amber};
    font-weight: 600;
  }
`;

const FactGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin: 2.5rem 0;

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FactCard = styled(motion(HudPanel))`
  padding: 1.25rem 1rem;
  text-align: center;
  background: rgba(6, 9, 17, 0.68);
`;

const FactValue = styled.div`
  font-family: ${fonts.display};
  font-size: 1.6rem;
  color: ${colors.amber};
  letter-spacing: 0.03em;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const FactLabel = styled.div`
  font-family: ${fonts.mono};
  font-size: 0.65rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${colors.textFaint};
  margin-top: 0.4rem;
`;

const LogLine = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-family: ${fonts.mono};
  font-size: 0.72rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${colors.textFaint};
  margin-top: 2.5rem;
`;

export function About() {
  const prefersReducedMotion = useReducedMotion();
  // When the user prefers reduced motion, skip the hidden/animate dance
  // entirely so content is simply present — no transform, no delay.
  const reveal = prefersReducedMotion
    ? { initial: 'show', whileInView: 'show', viewport: revealViewport }
    : { initial: 'hidden', whileInView: 'show', viewport: revealViewport };

  return (
    <Wrap>
      <SectionBackdrop
        $image={aboutImg}
        role="img"
        aria-label="A backlit, eclipsed planet with a thin crescent rim of light, a small moon, and an ember-orange nebula streak — a quiet, contemplative view of deep space"
      />
      <Main>
        <Eyebrow>
          <StatusDot />
          FILE 01 — HOUSE PROFILE
        </Eyebrow>
        <Title>About</Title>

        <Panel {...reveal} variants={fadeUp}>
          <Text>
            Mission Control is <strong>San Francisco's oldest hacker house</strong> — 10+ years
            running, community-run with no corporate operator. Decisions are made by resident
            vote, a "commune" model that has kept the house resident-led since day one.
          </Text>
          <Text>
            Founded in <strong>2013</strong>, the house began as summer housing for the Thiel
            Fellowship's 2014 class. It has since grown into a full-time home base for founders,
            engineers, and builders working on hard problems.
          </Text>
          <Text>
            The house holds <strong>10 bedrooms</strong> across roughly{' '}
            <strong>6,400 sq ft</strong> in the heart of the Mission District. Every room
            currently carries a waitlist of roughly <strong>40 people</strong> deep.
          </Text>
        </Panel>

        <FactGrid {...reveal} variants={staggerContainer()}>
          <FactCard variants={fadeUp}>
            <FactValue>10+</FactValue>
            <FactLabel>Years Running</FactLabel>
          </FactCard>
          <FactCard variants={fadeUp}>
            <FactValue>10</FactValue>
            <FactLabel>Bedrooms</FactLabel>
          </FactCard>
          <FactCard variants={fadeUp}>
            <FactValue>~6,400</FactValue>
            <FactLabel>Sq Ft</FactLabel>
          </FactCard>
        </FactGrid>

        <LogLine {...reveal} variants={fadeUp}>
          <StatusDot $color={colors.cyan} />
          LOG: HOUSE MODEL — COMMUNE / RESIDENT VOTE — EST. 2013
        </LogLine>
      </Main>
    </Wrap>
  );
}
